import React from "react";
import { Col, Row, Button } from "react-bootstrap";
import {
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Strings } from "../../resources/i18n/i18n";
import { OPERATION_STATUS } from "../../config/axios.init";
import {
  validateMinimartCityFixAction,
  applyMinimartCityFixAction,
} from "./DatabaseTableAction";
import { StyledHeadCell, StyledBodyCell, StyledRow } from "./Constants";
import "./DatabaseTables.scss";

// Colours the per-column outcome so a screenful of rows can be scanned quickly.
const STATUS_COLOURS = {
  UPDATED: "success",
  WOULD_UPDATE: "info",
  ALREADY_CLEAN: "default",
  NO_ADDRESS_ROW: "warning",
};

const ROW_COLUMNS = [
  { key: "row", label: "cityFixRow" },
  { key: "minimartId", label: "cityFixMinimartId" },
  { key: "sheetName", label: "sheetName" },
  { key: "dbName", label: "dbName" },
  { key: "currentCity", label: "currentCity" },
  { key: "currentAddressCity", label: "currentAddressCity" },
  { key: "newCity", label: "newCity" },
];

/**
 * Uploads the business-provided Excel of corrected minimart city names.
 *
 * The sheet is matched on minimart_id alone — the name column is not enforced —
 * so this screen is deliberately built as validate-then-apply: Apply stays
 * disabled until a dry run has been read, and the report shows the sheet's name
 * next to the database name on every row so a misaligned sheet is visible.
 */
class MinimartCityFix extends React.Component {
  constructor(props) {
    super(props);
    this.fileInput = React.createRef();
    this.state = {
      file: null,
      loading: false,
      // Set by a successful dry run; cleared whenever the chosen file changes.
      validatedReport: null,
      report: null,
      confirmApply: false,
      successMessage: "",
      errorMessage: "",
    };
  }

  handleFileChange = (event) => {
    const file = event.target.files?.[0] ?? null;
    // A new file invalidates the previous dry run, so Apply locks again.
    this.setState({
      file,
      validatedReport: null,
      report: null,
      successMessage: "",
      errorMessage: "",
    });
  };

  handleValidate = () => {
    this.runUpload(validateMinimartCityFixAction, true);
  };

  handleApply = () => {
    this.setState({ confirmApply: false }, () => {
      this.runUpload(applyMinimartCityFixAction, false);
    });
  };

  runUpload = (action, isDryRun) => {
    const { file } = this.state;
    if (!file) {
      this.setState({ errorMessage: Strings("minimartCityFixSelectFile") });
      return;
    }

    this.setState({ loading: true, successMessage: "", errorMessage: "" }, () => {
      action(file, (response) => {
        if (response.status === OPERATION_STATUS.SUCCESS) {
          const report = response.data.data;
          this.setState({
            loading: false,
            report,
            validatedReport: isDryRun ? report : null,
            successMessage: isDryRun
              ? Strings("minimartCityFixDryRunDone")
              : Strings("minimartCityFixApplyDone"),
          });
        } else {
          this.setState({
            loading: false,
            errorMessage:
              response.error?.message || Strings("errorFetchingData"),
          });
        }
      });
    });
  };

  renderMessages = () => {
    const { successMessage, errorMessage } = this.state;
    return (
      <>
        {successMessage && (
          <Row className="mb-2">
            <Col>
              <div className="successMessage">{successMessage}</div>
            </Col>
          </Row>
        )}
        {errorMessage && (
          <Row className="mb-2">
            <Col>
              <div className="errors p-2 errorMessage">{errorMessage}</div>
            </Col>
          </Row>
        )}
      </>
    );
  };

  renderUploadBar = () => {
    const { file, loading, validatedReport } = this.state;
    return (
      <Row className="align-items-center">
        <Col md={12} className="pb-2">
          <div className="reversalNote">
            <WarningAmberIcon className="reversalNoteIcon" />
            <div className="reversalNoteBody">
              <div className="reversalNoteTitle">{Strings("MinimartCityFix")}</div>
              <div className="reversalNoteText">
                {Strings("minimartCityFixDescription")}
              </div>
              <div className="reversalNoteText">
                {Strings("minimartCityFixValidateFirst")}
              </div>
            </div>
          </div>
        </Col>

        <Col md="auto" className="pt-2">
          <Button
            className="buttonBackground"
            onClick={() => this.fileInput.current.click()}
            disabled={loading}
          >
            {Strings("Browse")}
          </Button>
          <input
            ref={this.fileInput}
            type="file"
            className="collapseButton"
            accept=".xlsx,.xls"
            // Clearing the value lets the same file be picked again after a failed run.
            onClick={(event) => {
              event.target.value = null;
            }}
            onChange={this.handleFileChange}
          />
        </Col>

        <Col md={4} className="pt-2">
          <div className="listLabel">
            {file ? file.name : Strings("minimartCityFixSelectFile")}
          </div>
        </Col>

        <Col md="auto" className="pt-2">
          <Button
            className="buttonBackground"
            onClick={this.handleValidate}
            disabled={!file || loading}
          >
            {Strings("minimartCityFixValidate")}
          </Button>
        </Col>

        <Col md="auto" className="pt-2">
          {/* Apply is gated on a dry run of the currently selected file. */}
          <Tooltip
            title={validatedReport ? "" : Strings("minimartCityFixValidateFirst")}
          >
            <span>
              <Button
                className="buttonBackground"
                onClick={() => this.setState({ confirmApply: true })}
                disabled={!validatedReport || loading}
              >
                {Strings("minimartCityFixApply")}
              </Button>
            </span>
          </Tooltip>
        </Col>

        {loading && (
          <Col md="auto" className="pt-2">
            <CircularProgress size={22} />
          </Col>
        )}
      </Row>
    );
  };

  renderSummary = () => {
    const { report } = this.state;
    return (
      <div className="listLabel pt-2">
        {Strings("minimartCityFixSummary", {
          total: report.totalRows,
          minimart: report.minimartCityUpdated,
          address: report.minimartAddressCityUpdated,
          skipped: report.skipped?.length ?? 0,
        })}
      </div>
    );
  };

  renderStatusChip = (status) => (
    <Chip
      size="small"
      label={status}
      color={STATUS_COLOURS[status] ?? "default"}
    />
  );

  renderRowsTable = () => {
    const { report } = this.state;
    if (!report.rows?.length) {
      return null;
    }
    return (
      <>
        <div className="listLabel pt-3 pb-1">{Strings("minimartCityFixRows")}</div>
        <TableContainer component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {ROW_COLUMNS.map((column) => (
                  <StyledHeadCell key={column.key}>
                    {Strings(column.label)}
                  </StyledHeadCell>
                ))}
                <StyledHeadCell>{Strings("minimartStatus")}</StyledHeadCell>
                <StyledHeadCell>{Strings("minimartAddressStatus")}</StyledHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.rows.map((row) => {
                // The name is not enforced server-side, so flag a mismatch here.
                const nameMismatch =
                  (row.sheetName ?? "").trim().toUpperCase() !==
                  (row.dbName ?? "").trim().toUpperCase();
                return (
                  <StyledRow key={row.row}>
                    {ROW_COLUMNS.map((column) => (
                      <StyledBodyCell key={column.key}>
                        {column.key === "dbName" && nameMismatch ? (
                          <Tooltip title={Strings("minimartCityFixNameMismatch")}>
                            <span className="text-danger">{row[column.key]}</span>
                          </Tooltip>
                        ) : (
                          row[column.key]
                        )}
                      </StyledBodyCell>
                    ))}
                    <StyledBodyCell>
                      {this.renderStatusChip(row.minimartStatus)}
                    </StyledBodyCell>
                    <StyledBodyCell>
                      {this.renderStatusChip(row.minimartAddressStatus)}
                    </StyledBodyCell>
                  </StyledRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  renderSkippedTable = () => {
    const { report } = this.state;
    if (!report.skipped?.length) {
      return null;
    }
    return (
      <>
        <div className="listLabel pt-3 pb-1">
          {Strings("minimartCityFixSkipped")}
        </div>
        <TableContainer component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <StyledHeadCell>{Strings("cityFixRow")}</StyledHeadCell>
                <StyledHeadCell>{Strings("cityFixMinimartId")}</StyledHeadCell>
                <StyledHeadCell>{Strings("sheetName")}</StyledHeadCell>
                <StyledHeadCell>{Strings("cityFixReason")}</StyledHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.skipped.map((row, index) => (
                <StyledRow key={`${row.row}-${index}`}>
                  <StyledBodyCell>{row.row}</StyledBodyCell>
                  <StyledBodyCell>{row.minimartId}</StyledBodyCell>
                  <StyledBodyCell>{row.sheetName}</StyledBodyCell>
                  <StyledBodyCell>
                    <Chip size="small" color="warning" label={row.reason} />
                  </StyledBodyCell>
                </StyledRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  /**
   * Cities still holding the corruption marker that this sheet does not cover.
   * Surfacing them is the completeness check — an empty list means the
   * correction is done, a populated one means more rows need a sheet entry.
   */
  renderRemainingTable = () => {
    const { report } = this.state;
    if (!report.remainingCorrupted?.length) {
      return (
        <div className="listLabel pt-3">
          {Strings("minimartCityFixNoRemaining")}
        </div>
      );
    }
    return (
      <>
        <div className="listLabel pt-3 pb-1">
          {Strings("minimartCityFixRemaining")}
        </div>
        {report.remainingCorruptedTruncated && (
          <div className="errors p-2 errorMessage mb-2">
            {Strings("minimartCityFixRemainingTruncated")}
          </div>
        )}
        <TableContainer component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <StyledHeadCell>{Strings("cityFixTable")}</StyledHeadCell>
                <StyledHeadCell>{Strings("cityFixMinimartId")}</StyledHeadCell>
                <StyledHeadCell>{Strings("currentCity")}</StyledHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.remainingCorrupted.map((row, index) => (
                <StyledRow key={`${row.table}-${row.minimartId}-${index}`}>
                  <StyledBodyCell>{row.table}</StyledBodyCell>
                  <StyledBodyCell>{row.minimartId}</StyledBodyCell>
                  <StyledBodyCell>{row.city}</StyledBodyCell>
                </StyledRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  renderConfirmDialog = () => {
    const { confirmApply, validatedReport } = this.state;
    if (!validatedReport) {
      return null;
    }
    return (
      <Dialog
        open={confirmApply}
        onClose={() => this.setState({ confirmApply: false })}
      >
        <DialogTitle>{Strings("minimartCityFixApply")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {Strings("minimartCityFixSummary", {
              total: validatedReport.totalRows,
              minimart: validatedReport.minimartCityUpdated,
              address: validatedReport.minimartAddressCityUpdated,
              skipped: validatedReport.skipped?.length ?? 0,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            className="buttonBackground"
            onClick={() => this.setState({ confirmApply: false })}
          >
            {Strings("Cancel")}
          </Button>
          <Button className="buttonBackground" onClick={this.handleApply}>
            {Strings("cityFixConfirm")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  render() {
    const { report } = this.state;

    return (
      <div className="duplicateEntryManagement">
        <div className="headerText">{Strings("MinimartCityFix")}</div>

        <Row className="paper">
          <Col md={12} className="pb-2">
            {this.renderUploadBar()}
          </Col>
          {this.renderMessages()}
          <Col md={12} className="ps-0 pe-0 pb-2 pt-2">
            <hr className="newhr opacity-100" />
          </Col>

          {report && (
            <Col md={12}>
              {this.renderSummary()}
              {this.renderRowsTable()}
              {this.renderSkippedTable()}
              {this.renderRemainingTable()}
            </Col>
          )}
        </Row>

        {this.renderConfirmDialog()}
      </div>
    );
  }
}

export default MinimartCityFix;
